import React from 'react';
import {
	GOAL_CATEGORY_OPTIONS,
	GOAL_CATEGORY_UNSET,
	isGoalCategorySelected
} from '../../constants/fitnessProfileGoals';
import { todayIsoDate } from '../../utils/fitnessProfileBloodPressure';
import {
	fitnessProfileDateFieldShellClassName,
	fitnessProfileDateInputClassName
} from './shared/fitnessProfileReadingStyles';

const inputClassName =
	'block w-full rounded-xl border border-gray-300 dark:border-dark-border bg-white dark:bg-dark-bg px-3 py-2 text-sm text-gray-900 dark:text-dark-text outline-none transition placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:border-blue-500 focus:ring-2 focus:ring-blue-500 dark:focus:border-blue-400 dark:focus:ring-blue-400';

const selectClassName =
	'block w-full rounded-xl border border-gray-300 dark:border-dark-border bg-white dark:bg-dark-bg px-3 py-2 text-sm text-gray-900 dark:text-dark-text outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-500 dark:focus:border-blue-400 dark:focus:ring-blue-400';

function Field({ id, label, required = false, error, children }) {
	return (
		<label htmlFor={id} className="block text-sm">
			<span className="font-medium text-gray-700 dark:text-dark-text">
				{label}
				{required && <span className="text-red-600 dark:text-red-400"> *</span>}
			</span>
			<div className="mt-1.5">{children}</div>
			{error && (
				<p id={`${id}-error`} className="mt-1.5 text-xs font-medium text-red-600 dark:text-red-400">
					{error}
				</p>
			)}
		</label>
	);
}

function categorySelectValue(row) {
	if (!row.id) {
		return isGoalCategorySelected(row.category) ? row.category : GOAL_CATEGORY_UNSET;
	}
	return isGoalCategorySelected(row.category) ? row.category : GOAL_CATEGORY_UNSET;
}

export default function FitnessProfileGoalFormRow({
	row,
	index,
	fieldErrors,
	onRowChange,
	onRemove,
	rowLabel,
	showRemove = true
}) {
	return (
		<div className="rounded-xl border border-gray-200 bg-gray-50 p-4 space-y-3 dark:border-dark-border dark:bg-dark-bg">
			<div className="flex items-center justify-between gap-2">
				<p className="text-sm font-semibold text-gray-900 dark:text-dark-text">{rowLabel}</p>
				{showRemove && (
					<button
						type="button"
						onClick={onRemove}
						className="text-xs font-medium text-red-600 dark:text-red-400"
					>
						Remove
					</button>
				)}
			</div>

			<Field
				id={`goal-category-${index}`}
				label="Category"
				required
				error={fieldErrors[`goal_category_${index}`]}
			>
				<select
					id={`goal-category-${index}`}
					value={categorySelectValue(row)}
					onChange={(event) => onRowChange(index, 'category', event.target.value)}
					className={selectClassName}
				>
					<option value={GOAL_CATEGORY_UNSET}>Select a category...</option>
					{GOAL_CATEGORY_OPTIONS.map((option) => (
						<option key={option.value} value={option.value}>
							{option.label}
						</option>
					))}
				</select>
			</Field>

			<Field
				id={`goal-outcome-${index}`}
				label="Desired outcome"
				required
				error={fieldErrors[`goal_outcome_${index}`]}
			>
				<input
					id={`goal-outcome-${index}`}
					value={row.outcome}
					onChange={(event) => onRowChange(index, 'outcome', event.target.value)}
					placeholder="Lose 15 lbs by summer"
					className={inputClassName}
				/>
			</Field>

			<div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
				<Field
					id={`goal-target-${index}`}
					label="Target metric"
					required
					error={fieldErrors[`goal_target_${index}`]}
				>
					<input
						id={`goal-target-${index}`}
						value={row.targetMetric}
						onChange={(event) => onRowChange(index, 'targetMetric', event.target.value)}
						placeholder={
							row.category === 'weight_loss' || row.category === 'weight_gain'
								? '175 lbs'
								: row.category === 'blood_pressure'
									? '120/80'
									: '3 workouts / week'
						}
						className={inputClassName}
					/>
				</Field>
				<Field
					id={`goal-date-${index}`}
					label="Target date"
					required
					error={fieldErrors[`goal_target_date_${index}`]}
				>
					<div className="min-w-0 w-full">
						<div
							className={fitnessProfileDateFieldShellClassName(
								Boolean(fieldErrors[`goal_target_date_${index}`])
							)}
						>
							<input
								id={`goal-date-${index}`}
								type="date"
								min={todayIsoDate()}
								value={row.targetDate}
								onChange={(event) => onRowChange(index, 'targetDate', event.target.value)}
								className={fitnessProfileDateInputClassName}
							/>
						</div>
					</div>
				</Field>
			</div>

			{(row.category === 'weight_loss' || row.category === 'weight_gain') && (
				<p className="text-xs text-gray-500 dark:text-gray-400">
					Starting weight comes from your logged weights. Put your target in target metric (e.g. 175
					lbs). Same as today or wording like “maintain” / “keep steady” tracks holding that weight,
					not losing or gaining.
				</p>
			)}
			{row.category === 'blood_pressure' && (
				<p className="text-xs text-gray-500 dark:text-gray-400">
					Starting BP comes from your logged readings. Use systolic/diastolic (e.g. 120/80) — same as
					today is fine for a stay-steady goal.
				</p>
			)}
		</div>
	);
}
